import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { noteBasisQuery } from '../../../api/queries';
import type {
  Branch,
  Draft,
  InvoiceKind,
  OrgActivityCode,
  OrgCurrency,
  Organization,
} from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { DateField } from '../../../ui/DatePicker';
import { DecimalField } from '../../../ui/DecimalField';
import { SelectField } from '../../../ui/Select';
import { TextField } from '../../../ui/TextField';
import styles from '../Sales.module.css';
import type { EditorMode } from './steps';

type Setter = <K extends keyof Draft>(key: K, value: Draft[K] | undefined) => void;

/**
 * Block 11 (D-2, notes-editor.md:84-89): on a note the currency, the branch and the activity code
 * are taken from the reference invoice and shown as read-only text with the «مأخوذة من الفاتورة
 * المرجعية» tag, not as inputs. The exchange rate stays the only input of the money group.
 *
 * The tag only appears when the value genuinely comes from the reference (`fromReference`). A note
 * whose reference carries no branch or activity code of its own falls back to the server-locked
 * value the draft will carry, shown without the tag — never an org default presented as the
 * reference's (the slice-5 honest-state fix).
 */
function FromReference({
  label,
  value,
  fieldName,
  fromReference,
}: {
  label: string;
  value: ReactNode;
  fieldName: string;
  fromReference: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.readOnlyField} data-field={fieldName} data-readonly="true">
      <span className={styles.readOnlyLabel}>{label}</span>
      <span className={styles.readOnlyValue}>{value}</span>
      {fromReference ? (
        <span className={styles.readOnlyNote}>{t.editor.note.fromReferenceField}</span>
      ) : null}
    </div>
  );
}

const REFERENCES = [
  ['purchaseOrderRef', 'poRef'],
  ['purchaseOrderDescription', 'poDesc'],
  ['salesOrderRef', 'soRef'],
  ['salesOrderDescription', 'soDesc'],
  ['proformaInvoiceNumber', 'proforma'],
] as const;

/**
 * Step 2, «تفاصيل المستند» (BLOCK-1-REDESIGN §A.2): four groups, each a fieldset with a legend.
 * Dates; the document currency and its rate (before the unit prices of step 3, CT-B1); the issuer
 * (activity code and branch, beside the issuer's own details); and the five order references,
 * always open and optional (role 4 RD4-02).
 */
export function DetailsStep({
  orgId,
  draft,
  kind,
  mode = 'invoice',
  issueOf,
  setField,
  onCurrency,
  organization,
  branches,
  activities,
  currencies,
}: {
  orgId: string;
  draft: Draft;
  kind: InvoiceKind;
  /** Block 11: a note locks the currency, branch and activity code to the reference (D-2). */
  mode?: EditorMode;
  issueOf: (path: string) => string | undefined;
  setField: Setter;
  onCurrency: (currency: string) => void;
  organization: Organization | undefined;
  branches: Branch[] | undefined;
  activities: OrgActivityCode[] | undefined;
  currencies: OrgCurrency[] | undefined;
}) {
  const { t, pick } = useI18n();
  const isNote = mode === 'note';
  // D-2 (slice-5 honest-state fix): on an unsaved note the draft carries org defaults for branch
  // and activity code, but the note is issued with the reference's. Read the first reference's own
  // branch and activity code from `note-basis` so step 2 shows the real locked values, not the
  // defaults, under the «من الفاتورة المرجعية» tag.
  const firstReference = draft.references?.[0];
  const referenceBasis = useQuery({
    ...noteBasisQuery(orgId, firstReference ?? '', kind),
    enabled: isNote && Boolean(firstReference),
  });
  const referenceBranchId = referenceBasis.data?.document.branchId ?? null;
  const referenceActivityCode = referenceBasis.data?.document.activityCode ?? null;
  // The reference's own value when it has one; otherwise the value the server will lock the note to
  // (the draft's) — shown without the from-reference tag so an org default is never mislabelled.
  const branchFromReference = isNote && referenceBranchId !== null;
  const activityFromReference = isNote && referenceActivityCode !== null;
  const branchId = branchFromReference ? referenceBranchId : draft.branchId;
  const activityCode = activityFromReference ? referenceActivityCode : draft.activityCode;
  const branch = branches?.find((b) => b.id === branchId);
  const activity = activities?.find((a) => a.code === activityCode);
  const currency = currencies?.find((c) => c.code === draft.currency);
  const currencyLabel = currency
    ? `${currency.code} — ${pick(currency.nameAr, currency.nameEn)}`
    : draft.currency;
  return (
    <div className={styles.stepBody}>
      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>{t.editor.groups.dates}</legend>
        <div className={styles.fieldGrid}>
          <DateField
            label={t.editor.settings.issueDate}
            description={t.editor.settings.issueDateHint}
            fieldName="issueDate"
            value={draft.issueDate ?? null}
            onChange={(v) => setField('issueDate', v ?? undefined)}
            errorMessage={issueOf('issueDate') ?? issueOf('dateTimeIssued')}
          />
          <DateField
            label={t.editor.settings.dueDate}
            fieldName="dueDate"
            value={draft.dueDate ?? null}
            onChange={(v) => setField('dueDate', v ?? undefined)}
            errorMessage={issueOf('dueDate')}
            isOptional
          />
          <DateField
            label={t.editor.settings.serviceDate}
            fieldName="serviceDeliveryDate"
            value={draft.serviceDeliveryDate ?? null}
            onChange={(v) => setField('serviceDeliveryDate', v ?? undefined)}
            errorMessage={issueOf('serviceDeliveryDate')}
            isRequired={kind === 'ei'}
            isOptional={kind !== 'ei'}
          />
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>{t.editor.groups.money}</legend>
        <div className={styles.fieldGrid}>
          {isNote ? (
            <FromReference
              label={t.editor.settings.currency}
              value={currencyLabel}
              fieldName="currency"
              fromReference
            />
          ) : (
            <SelectField
              label={t.editor.settings.currency}
              fieldName="currency"
              value={draft.currency}
              onChange={(v) => v && onCurrency(v)}
              options={(
                currencies ?? [
                  { code: 'EGP', isPrimary: true, nameAr: null, nameEn: 'Egyptian Pound' },
                ]
              ).map((c) => ({ id: c.code, label: `${c.code} — ${pick(c.nameAr, c.nameEn)}` }))}
              errorMessage={issueOf('currency')}
            />
          )}
          {draft.currency !== 'EGP' ? (
            <DecimalField
              label={t.editor.settings.exchangeRate}
              description={t.editor.settings.rateHint(draft.currency)}
              fieldName="exchangeRate"
              value={draft.exchangeRate ?? ''}
              onChange={(v) => setField('exchangeRate', v)}
              isRequired
              errorMessage={issueOf('exchangeRate')}
            />
          ) : null}
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>{t.editor.sections.issuer}</legend>
        <div className={styles.issuerRow}>
          <div className={styles.fieldGrid}>
            {isNote ? (
              <>
                <FromReference
                  label={t.editor.settings.activityCode}
                  value={
                    activity ? `${activity.code} — ${pick(activity.nameAr, activity.nameEn)}` : '—'
                  }
                  fieldName="activityCode"
                  fromReference={activityFromReference}
                />
                <FromReference
                  label={t.editor.settings.branch}
                  value={
                    branch ? (
                      <>
                        <UserText>{branch.name}</UserText>
                        {` (${branch.eta_branch_id})`}
                      </>
                    ) : (
                      '—'
                    )
                  }
                  fieldName="branchId"
                  fromReference={branchFromReference}
                />
              </>
            ) : (
              <>
                <SelectField
                  label={t.editor.settings.activityCode}
                  fieldName="activityCode"
                  value={draft.activityCode ?? null}
                  onChange={(v) => setField('activityCode', v ?? undefined)}
                  options={(activities ?? []).map((a) => ({
                    id: a.code,
                    label: `${a.code} — ${pick(a.nameAr, a.nameEn)}`,
                  }))}
                  isRequired
                  errorMessage={issueOf('activityCode')}
                />
                <SelectField
                  label={t.editor.settings.branch}
                  fieldName="branchId"
                  value={draft.branchId ?? null}
                  onChange={(v) => setField('branchId', v ?? undefined)}
                  options={(branches ?? [])
                    .filter((b) => b.status === 'active')
                    .map((b) => ({ id: b.id, label: `${b.name} (${b.eta_branch_id})` }))}
                  isRequired
                  errorMessage={issueOf('branchId')}
                />
              </>
            )}
          </div>
          <div className={styles.issuerPanel} data-testid="issuer-block">
            <span className={styles.customerName}>
              {organization ? (
                <UserText>{organization.legalName ?? organization.name}</UserText>
              ) : (
                '—'
              )}
            </span>
            {organization?.rin ? <span className={styles.mono}>{organization.rin}</span> : null}
            {organization?.email ? (
              <span className={styles.mono} data-testid="issuer-email">
                {organization.email}
              </span>
            ) : null}
            {organization?.phone ? <span className={styles.mono}>{organization.phone}</span> : null}
            {branch ? (
              <span>
                <UserText>{branch.name}</UserText>
                {` · ${branch.building_number} ${branch.street}، ${branch.region_city}، ${branch.governate}`}
              </span>
            ) : null}
          </div>
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>
          {t.editor.sections.additional}{' '}
          <span className={styles.optionalTag}>{t.common.optional}</span>
        </legend>
        <div className={styles.fieldGrid}>
          {REFERENCES.map(([key, label]) => (
            <TextField
              key={key}
              label={t.editor.additional[label]}
              fieldName={key}
              value={draft[key] ?? ''}
              onChange={(v) => setField(key, v)}
              isOptional
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}
